import BaseTextField, { TextFieldProps } from '@mui/material/TextField';
import React from 'react';

const TextField = React.forwardRef<HTMLDivElement, TextFieldPropTypes>(
  (
    {
      disableUnderline = true,
      startAdornment,
      endAdornment,
      slotProps,
      fullWidth = true,
      success,
      ...props
    },
    ref,
  ) => {
    // Filter out empty objects that Storybook might pass
    // Empty objects {} are not valid React children
    const isValidAdornment = (node: any): boolean => {
      if (node === null || node === undefined) return false;
      if (React.isValidElement(node)) return true;
      if (Array.isArray(node)) return true;
      if (typeof node === 'string' || typeof node === 'number') return true;
      // Filter out plain objects (like {} from Storybook)
      if (typeof node === 'object' && Object.keys(node).length === 0) return false;
      return false;
    };

    // Merge our custom slotProps with any provided slotProps
    // This allows DatePicker and other components to pass their own slotProps
    const baseSlotProps = (
      slotProps && typeof slotProps === 'object' ? slotProps : {}
    ) as any;
    const mergedSlotProps = {
      ...baseSlotProps,
      input: {
        ...(baseSlotProps?.input || {}),
        // Preserve any existing classes
        classes: baseSlotProps?.input?.classes,
        // Use provided disableUnderline or fall back to our default
        disableUnderline: baseSlotProps?.input?.disableUnderline ?? disableUnderline,
        // Merge adornments - prefer our props, then external, then undefined
        // Filter out invalid values (like empty objects from Storybook)
        startAdornment: isValidAdornment(startAdornment)
          ? startAdornment
          : isValidAdornment(baseSlotProps?.input?.startAdornment)
            ? baseSlotProps?.input?.startAdornment
            : undefined,
        endAdornment: isValidAdornment(endAdornment)
          ? endAdornment
          : isValidAdornment(baseSlotProps?.input?.endAdornment)
            ? baseSlotProps?.input?.endAdornment
            : undefined,
        // Apply success styling if success prop is true
        ...(success && {
          sx: {
            ...(baseSlotProps?.input?.sx || {}),
            borderColor: (theme: any) => theme.palette.success.main,
            '&.Mui-focused': {
              borderColor: (theme: any) => theme.palette.success.main,
            },
          },
        }),
      },
      inputLabel: {
        ...(baseSlotProps?.inputLabel || {}),
        // Allow shrink to be determined automatically by MUI unless explicitly set
        // This enables the floating label behavior (centered when empty, shrunken when focused/filled)
        ...(baseSlotProps?.inputLabel?.shrink !== undefined
          ? { shrink: baseSlotProps?.inputLabel?.shrink }
          : {}),
      },
      formHelperText: {
        ...(baseSlotProps?.formHelperText || {}),
        classes: baseSlotProps?.formHelperText?.classes,
      },
    };

    return (
      <BaseTextField
        ref={ref}
        variant="filled"
        fullWidth={fullWidth}
        {...props}
        slotProps={mergedSlotProps}
      />
    );
  },
);

TextField.displayName = 'TextField';

export type TextFieldPropTypes = TextFieldProps & {
  disableUnderline?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  success?: boolean;
};

export default TextField;
